/**
 * Período dos relatórios e das listas de pedidos: "hoje", "ontem", "últimos 7 dias", "esta semana",
 * "semana passada", "mês atual", "mês passado" ou datas livres. Puro e sem I/O.
 *
 * Tudo é calendário (`AAAA-MM-DD`, dia em São Paulo): o período vai para o servidor como `from`/`to`
 * inclusivos e o filtro roda no banco. A semana começa na segunda-feira.
 */
import { addDays, addMonths, diffDays } from "../../../packages/financial/src/cashflow.js";

export type PeriodKey = "hoje" | "ontem" | "7d" | "semana" | "semana-passada" | "mes" | "mes-passado" | "custom";

export const PERIOD_OPTIONS: ReadonlyArray<{ value: PeriodKey; label: string }> = [
  { value: "hoje", label: "Hoje" },
  { value: "ontem", label: "Ontem" },
  { value: "7d", label: "Últimos 7 dias" },
  { value: "semana", label: "Esta semana" },
  { value: "semana-passada", label: "Semana passada" },
  { value: "mes", label: "Mês atual" },
  { value: "mes-passado", label: "Mês passado" },
  { value: "custom", label: "Personalizado" }
];

/** Intervalo que vai ao servidor: `AAAA-MM-DD` inclusivos, dia em São Paulo. */
export interface OrdersRange {
  from: string;
  to: string;
}

export interface PeriodSelection {
  key: PeriodKey;
  /** Só para `custom`. */
  from?: string;
  to?: string;
}

export interface ResolvedPeriod {
  key: PeriodKey;
  from: string;
  to: string;
  label: string;
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const isDay = (value: unknown): value is string => {
  if (typeof value !== "string" || !ISO.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
};

/** Segunda-feira da semana que contém `iso`. */
export function weekStart(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = domingo
  return addDays(iso, -((weekday + 6) % 7));
}

export function resolvePeriod(selection: PeriodSelection, today: string): ResolvedPeriod {
  const label = PERIOD_OPTIONS.find((option) => option.value === selection.key)?.label ?? "Período";
  const make = (from: string, to: string): ResolvedPeriod => ({ key: selection.key, from, to, label });
  switch (selection.key) {
    case "hoje":
      return make(today, today);
    case "ontem":
      return make(addDays(today, -1), addDays(today, -1));
    case "7d":
      return make(addDays(today, -6), today);
    case "semana": {
      const start = weekStart(today);
      return make(start, addDays(start, 6));
    }
    case "semana-passada": {
      const start = addDays(weekStart(today), -7);
      return make(start, addDays(start, 6));
    }
    case "mes": {
      const start = `${today.slice(0, 7)}-01`;
      return make(start, addDays(addMonths(start, 1), -1));
    }
    case "mes-passado": {
      const thisMonth = `${today.slice(0, 7)}-01`;
      const start = addMonths(thisMonth, -1);
      return make(start, addDays(thisMonth, -1));
    }
    default: {
      // Datas livres: ausente ou inválida cai no dia de hoje; invertida é corrigida (de ≤ até).
      const from = isDay(selection.from) ? selection.from : today;
      const to = isDay(selection.to) ? selection.to : from;
      return from <= to ? make(from, to) : make(to, from);
    }
  }
}

/** Quantos dias o período cobre (inclusivo). */
export function periodDays(period: { from: string; to: string }): number {
  return diffDays(period.from, period.to) + 1;
}

export function periodFromSearch(params: URLSearchParams, fallback: PeriodKey): PeriodSelection {
  const raw = params.get("periodo");
  const key = PERIOD_OPTIONS.some((option) => option.value === raw) ? (raw as PeriodKey) : fallback;
  if (key !== "custom") return { key };
  return { key, from: params.get("de") ?? undefined, to: params.get("ate") ?? undefined };
}

/** Parâmetros de URL do período; o padrão da tela não precisa ir na URL. */
export function periodToSearch(selection: PeriodSelection, fallback: PeriodKey): Record<string, string> {
  if (selection.key === "custom") {
    return { periodo: "custom", ...(selection.from ? { de: selection.from } : {}), ...(selection.to ? { ate: selection.to } : {}) };
  }
  return selection.key === fallback ? {} : { periodo: selection.key };
}
